import React, { useState, useEffect } from "react";
import { Form, Button, Alert, Card, Row, Col } from "react-bootstrap";
import api from "common/api";
import { useAuth } from "authContext";
import { BsArrowLeft } from "react-icons/bs";
import { toast } from "react-toastify";

interface LoginFormProps {
  onSubmit: () => void;
  onBack: () => void;
}

const LoginRegisterFormLessons: React.FC<LoginFormProps> = ({
  onSubmit,
  onBack,
}) => {
  const { isAuthenticated, login } = useAuth();
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (isAuthenticated) {
      onSubmit();
    }
  }, [isAuthenticated, onSubmit]);

  if (isAuthenticated) {
    return null;
  }

  const toggleLoginMode = (mode: boolean) => {
    setIsLoginMode(mode);
    setError("");
  };

  const handleLogin = async () => {
    try {
      const response = await api.post(
        "/accounts/jwt/create/",
        { email, password },
        { skipRefreshToken: true },
      );

      login(response.data.access);

      localStorage.setItem("refreshToken", response.data.refresh);

      onSubmit();
    } catch (err) {
      setError("Prijava nije uspjela. Provjerite svoje podatke.");
      toast.error("Neispravni podaci za prijavu");
    }
  };

  const handleRegister = async () => {
    if (password !== confirmPassword) {
      setError("Lozinke se ne podudaraju");
      return;
    }
    try {
      await api.post("/accounts/users/", {
        role: "STUDENT",
        email,
        password,
        first_name: firstName,
        last_name: lastName,
      });
      await handleLogin();
    } catch (err: any) {
      if (
        err.response?.data?.email?.[0]?.includes(
          "user with this email address already exists",
        )
      ) {
        setError("Korisnik s ovom email adresom već postoji u sustavu.");
      } else if (err.response?.data?.password) {
        setError(
          "Provjerite lozinku. Mora sadržavati barem 8 znakova, ne smije sadržavati samo brojke ili biti slična imenu.",
        );
      } else {
        setError("Registracija nije uspjela.");
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (isLoginMode) {
      handleLogin();
    } else {
      handleRegister();
    }
  };

  return (
    <Card className="shadow p-3 mb-5 bg-white rounded">
      <Card.Body>
        <div className="d-flex align-items-center mb-4">
          <BsArrowLeft
            className="mr-2 lessons-cursor-pointer"
            size={24}
            onClick={onBack}
          />
          <h3 className="ml-3 mb-0">
            {isLoginMode ? "Prijava" : "Registriranje"}
          </h3>
        </div>

        {error && <Alert variant="primary">{error}</Alert>}
        <Form onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group controlId="formBasicEmail">
                <Form.Label>Email adresa</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Unesite email adresu"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group controlId="formBasicPassword">
                <Form.Label>Lozinka</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Lozinka"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
          </Row>
          {!isLoginMode && (
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group controlId="formConfirmPassword">
                  <Form.Label>Potvrdite lozinku</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Potvrdite lozinku"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
          )}
          {!isLoginMode && (
            <>
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group controlId="formFirstName">
                    <Form.Label>Ime</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Unesite ime"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group controlId="formLastName">
                    <Form.Label>Prezime</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Unesite prezime"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}
          <Button className="mt-3" variant="primary" type="submit">
            {isLoginMode ? "Prijava" : "Registriranje"}
          </Button>
        </Form>
        <div className="mt-3">
          {isLoginMode ? (
            <p>
              Nemaš korisnički račun?{" "}
              <strong>
                <a href="#" onClick={() => toggleLoginMode(false)}>
                  Registriraj se!
                </a>
              </strong>
            </p>
          ) : (
            <p>
              Već imate korisnički račun?{" "}
              <strong>
                <a href="#" onClick={() => toggleLoginMode(true)}>
                  Prijava
                </a>
              </strong>
            </p>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default LoginRegisterFormLessons;
